import { Chat } from "tf-api-client";
import { API_BASE_PATH, AWARENESS_ROUTE } from "../config/constants";

/**
 * Requests of the awareness page, the landing of the simulation emails (see
 * `docs/landing.md` of simulation_svc). The user isn't logged in: a landing
 * session, an HttpOnly cookie of the API domain scoped to one email, is the
 * only credential. These requests never go through the shared `api` client,
 * which would add the token of a user logged in on this browser.
 */

/** State of the awareness page: the campaign of the email, in its language. */
export type LandingState = {
  state: "active" | "expired";
  language: string;
  firstName: string;
};

/** An answer of the landing routes other than a success, with its status. */
export class LandingApiError extends Error {
  constructor(public readonly status: number) {
    super(`landing request failed with status ${status}`);
  }
}

let handoffCode: string | null = null;

/**
 * Takes the handoff code out of the URL of the awareness page, before anything
 * else runs (PostHog, the router): the code stays in memory only, and the
 * fragment leaves the address bar and the history. Call it once, first thing
 * at startup.
 */
export function captureHandoffCode(): void {
  if (window.location.pathname !== AWARENESS_ROUTE) return;
  const code = new URLSearchParams(window.location.hash.slice(1)).get("code");
  if (window.location.hash) {
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
  }
  handoffCode = code || null;
}

/** The handoff code captured at startup, once: null after the first call. */
export function takeHandoffCode(): string | null {
  const code = handoffCode;
  handoffCode = null;
  return code;
}

async function request(
  method: string,
  path: string,
  body?: unknown,
): Promise<Response> {
  const response = await fetch(API_BASE_PATH + path, {
    method,
    credentials: "include",
    headers:
      body === undefined
        ? { Accept: "application/json" }
        : { Accept: "application/json", "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new LandingApiError(response.status);
  return response;
}

/** Exchanges the handoff code for the landing session (the cookie). */
export async function startLandingSession(code: string): Promise<void> {
  await request("POST", "/simulation/landing/session", { code });
}

/** State of the landing session; a LandingApiError 401 without session. */
export async function getLandingState(): Promise<LandingState> {
  return (await request("GET", "/simulation/landing/state")).json();
}

/** First node of the awareness chat of the email, in the language. */
export async function startLandingChat(language: string): Promise<Chat.Node> {
  return (await request("POST", "/landing/chat/start", { language })).json();
}

/** A node of the awareness chat started by the email. */
export async function getLandingNode(
  nodeId: string,
  chatBlockId: string,
): Promise<Chat.Node> {
  const query = new URLSearchParams({ chat_block_id: chatBlockId });
  return (
    await request(
      "GET",
      `/landing/chat/nodes/${encodeURIComponent(nodeId)}?${query}`,
    )
  ).json();
}

/**
 * Sends a free-text message of the awareness chat; the answer streams as
 * `data: {"text": …}` lines, the format of the assistant chat.
 */
export async function sendLandingMessage(
  content: string,
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const response = await request("POST", "/landing/chat/messages", {
    content,
  });
  return response.body!.getReader();
}
